import type Handsontable from 'handsontable';
import type { CellChange, ChangeSource } from 'handsontable/common';
import type {
  DetailedSettings as ContextMenuDetailedSettings,
  Selection as ContextMenuSelection,
} from 'handsontable/plugins/contextMenu';

/** Raw Handsontable row/cell data for plan tables */
export type PlanTableData = unknown[][];
export type PlanTableRow = unknown[];

export type RowsWithErrors = {
  rowIndex: number;
  field: { index: number; message: string }[];
}[];

export type HandsontableWithClickTime = Handsontable & { __lastClickTime?: number };

export type CellProperties = Handsontable.CellProperties & {
  className?: string | string[];
  __originalRenderer?: RendererFunction;
  [key: string]: unknown;
};

export type RendererFunction = (
  instance: Handsontable.Core,
  td: HTMLElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties?: CellProperties
) => HTMLElement;

export type CellMeta = CellProperties;

export type MergeConfig = {
  row: number;
  col: number;
  rowspan: number;
  colspan: number;
};

export type SelectionTupleRange = [number, number, number, number];
export type SelectionObjectRange =
  | ContextMenuSelection
  | {
      start?: { row?: number; col?: number };
      end?: { row?: number; col?: number };
    };
export type SelectionRange = SelectionTupleRange | SelectionObjectRange;
export type SelectionPayload = SelectionRange[];

export type ExtendedContextMenuSettings = ContextMenuDetailedSettings & {
  beforeContextMenuShow?: (menu: unknown, coords: { row: number; col: number }) => boolean;
};

export type { CellChange, ChangeSource };

/** Resolve numeric column index from a Handsontable cell-change tuple */
export const getColIndexFromCellChange = (prop: CellChange[1]): number | null => {
  if (typeof prop === 'number' && Number.isFinite(prop)) {
    return prop;
  }
  if (typeof prop === 'string') {
    const parsed = Number(prop);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};
export type HandsontableColumnSettings = Handsontable.ColumnSettings;

export type { HotTableRef } from '@handsontable/react-wrapper';

export type CellsFunction = (row: number, col: number, prop: string | number) => CellMeta;

export interface CellPropsBase {
  className?: string | string[];
  readOnly?: boolean;
  editor?: string | boolean;
  disabled?: boolean;
  disableVisualSelection?: boolean;
  renderer?: RendererFunction;
  [key: string]: unknown;
}
